import { useState, useCallback } from 'react';
import { Row, Col, Alert } from 'react-bootstrap';
import { MdInfoOutline, MdEdit, MdOutlineDeleteOutline, MdDelete, MdAdd } from 'react-icons/md';
import { useQueryClient } from '@tanstack/react-query';
import { BPMTable } from '@Components/StickyTable/BPMTable';
import { Skeleton, TableSkeleton } from '@Components/Skeleton/Skeleton';
import { Button } from '@Components/Button/Button';
import { IconButton } from '@Components/IconButton';
import { Intent, Size } from '@Components/designConstants';
import { AlertDialog } from '@Components/AlertDialog/AlertDialog';
import { useAppSelector } from '@src/redux';
import { currentCompanySelector } from '@src/redux/company';
import { useSetError } from '@src/redux/modals';
import { useAlertPresets, useDeleteAlertPreset, AlertPreset } from './hooks/useAlertData';
import { formatTimeAgo } from './utils/alertUtils';
import { CreatePresetModal } from './CreatePresetModal';
import { EditPresetModal } from './EditPresetModal';
import './AnomalyDetection.scss';

const NoPresetsRow = () => (
  <div className='no-presets' data-testid='no-presets'>
    No presets configured
  </div>
);

export const PresetsTab = () => {
  const company = useAppSelector(currentCompanySelector);
  const queryClient = useQueryClient();
  const deletePreset = useDeleteAlertPreset();
  const setError = useSetError();

  const { data: presets = [], isLoading } = useAlertPresets(company, { forTable: true });

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [presetToDelete, setPresetToDelete] = useState<AlertPreset | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [presetToEdit, setPresetToEdit] = useState<AlertPreset | null>(null);

  const handleDeletePreset = useCallback(
    (presetId: string) => {
      const preset = presets.find(p => p.id === presetId);
      if (preset) {
        setPresetToDelete(preset);
        setShowDeleteConfirmation(true);
      }
    },
    [presets]
  );

  const confirmDeletePreset = useCallback(async () => {
    if (!presetToDelete) {
      return;
    }

    try {
      setDeletingId(presetToDelete.id);
      await deletePreset(presetToDelete.id);
      await queryClient.invalidateQueries({ queryKey: ['alertPresetsTable', company] });

      setShowDeleteConfirmation(false);
      setPresetToDelete(null);
    } catch (error) {
      setError({
        message: `Failed to delete preset: ${
          error instanceof Error ? error.message : String(error)
        }`,
        reportError: error
      });
    } finally {
      setDeletingId(null);
    }
  }, [presetToDelete, deletePreset, queryClient, company, setError]);

  const cancelDeletePreset = useCallback(() => {
    setShowDeleteConfirmation(false);
    setPresetToDelete(null);
  }, []);

  const handleEditPreset = useCallback(
    (presetId: string) => {
      const preset = presets.find(p => p.id === presetId);
      if (preset) {
        setPresetToEdit(preset);
        setShowEditModal(true);
      }
    },
    [presets]
  );

  const tableHeaders = [
    {
      name: 'preset_name',
      label: 'Preset Name',
      flex: 2,
      renderer: (data: AlertPreset) => (
        <div>
          <div>{data.preset_name}</div>
        </div>
      )
    },
    {
      name: 'description',
      label: 'Description',
      flex: 2,
      renderer: (data: AlertPreset) => (
        <div>
          <div>{data.description}</div>
        </div>
      )
    },
    {
      name: 'last_modified',
      label: 'Last Modified',
      flex: 1,
      renderer: (data: AlertPreset) => <span>{formatTimeAgo(data.last_modified)}</span>
    },
    {
      name: 'last_modified_by_email',
      label: 'Last Modified By',
      flex: 1,
      renderer: (data: AlertPreset) => (
        <span>{data.last_modified_by_email || data.created_by_email || 'Unknown'}</span>
      )
    },
    {
      name: 'id',
      label: 'Actions',
      flex: 1,
      renderer: (data: AlertPreset) => (
        <div className='action-buttons'>
          <IconButton
            size={Size.SM}
            onClick={() => handleEditPreset(data.id)}
            className='edit-btn'
            data-testid={`edit-preset-${data.id}`}
          >
            <MdEdit />
          </IconButton>
          <IconButton
            size={Size.SM}
            onClick={() => handleDeletePreset(data.id)}
            className='delete-btn'
            disabled={deletingId === data.id}
            data-testid={`delete-preset-${data.id}`}
          >
            <MdOutlineDeleteOutline />
          </IconButton>
        </div>
      )
    }
  ];

  return (
    <div className='anomaly-detection-content'>
      <Row>
        <Col>
          <Alert variant='info' className='info-alert'>
            <MdInfoOutline className='alert-icon' />
            <div>
              Welcome to <strong>Presets</strong>, manage your alert presets below.
            </div>
          </Alert>
        </Col>
      </Row>

      <Row>
        <Col>
          <div className='presets-actions'>
            <Button
              size={Size.SM}
              onClick={() => setShowCreateModal(true)}
              className='create-preset-btn'
              leadingElement={<MdAdd className='create-icon' />}
              data-testid='add-preset-button'
            >
              Add Budget Preset
            </Button>
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <div className='presets-table-container' data-testid='presets-table'>
            {isLoading ? (
              <Skeleton>
                <TableSkeleton />
              </Skeleton>
            ) : (
              <BPMTable headers={tableHeaders} data={presets} noRowsRenderer={NoPresetsRow} />
            )}
          </div>
        </Col>
      </Row>

      <AlertDialog
        intent={Intent.ERROR}
        show={showDeleteConfirmation}
        onHide={cancelDeletePreset}
        icon={<MdDelete size={95} />}
        title='Delete Preset?'
        body={`Are you sure you want to delete the preset "${presetToDelete?.preset_name}"? This action cannot be undone.`}
        primaryAction={{ onClick: confirmDeletePreset, label: 'Delete' }}
        secondaryAction={{ onClick: cancelDeletePreset }}
      />

      <CreatePresetModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSubmit={() => {
          queryClient.invalidateQueries({ queryKey: ['alertPresetsTable', company] });
        }}
      />

      <EditPresetModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setPresetToEdit(null);
        }}
        onSubmit={() => {
          queryClient.invalidateQueries({ queryKey: ['alertPresetsTable', company] });
        }}
        preset={presetToEdit}
      />
    </div>
  );
};
